/**
 * Iter 428.p5 — Create-Report modal for the Engineering Desk.
 *
 * Wraps the new POST /api/engineering/sessions/{sid}/generate-report
 * pipeline. Steps the user through:
 *
 *   1. Pick a report kind (pump assessment / technical / inspection /
 *      engineering note) — loaded from /api/engineering/report-kinds
 *      so backend and FE never drift.
 *   2. Title (defaults to session title).
 *   3. Link entity — 7 tabs (customer / lead / site / ticket / job
 *      card / sales order / project). Search per kind, click to pick.
 *   4. Live family-chain preview via GET /api/family/resolve — the
 *      user sees "Customer · Project · SO-1234 · ST-987 · JC-0142"
 *      the moment they pick an entity, so they know the full family
 *      that will be stamped on the generated document.
 *   5. Audience (customer / internal) + Length (short / medium / long).
 *   6. Optional extra instruction (surgical steer for the LLM).
 *   7. If link.kind === "project", offer a "Also open a planning
 *      workbench" toggle — when on, the backend spawns a fresh
 *      workbench_drafts row with kind=planning and copies the
 *      transcript across (Dirk's steer 2a).
 *   8. Generate → persists the PDF to R2 → inserts customer_documents
 *      row → returns { document, family, planning_draft_id }.
 *
 * The modal is intentionally self-contained — no shared state with the
 * parent session page beyond `sid`, `onClose`, and an `onGenerated`
 * callback so the parent can toast + optionally navigate.
 */
import { useEffect, useMemo, useState } from "react";
import {
  X, Loader2, Sparkles, FileText, Users, Building2, MapPin,
  Ticket, ClipboardList, FileSignature, FolderKanban,
} from "lucide-react";
import api from "../../lib/api";
import { toast } from "../ui/sonner";
import DictTextarea from "../DictTextarea";

// ─── Entity kinds you can link a generated report to. Order matches
// the family walker's precedence: the more specific, the further left.
const LINK_KINDS = [
  { key: "jobcard",     label: "Job card",    icon: ClipboardList,  endpoint: "/jobcards",         labelKey: "title",       codeKey: "jc_ref" },
  { key: "ticket",      label: "Ticket",      icon: Ticket,         endpoint: "/tickets",          labelKey: "subject",     codeKey: "ticket_ref" },
  { key: "sales_order", label: "Sales order", icon: FileSignature,  endpoint: "/sales-documents",  labelKey: "customer_name", codeKey: "doc_number" },
  { key: "project",     label: "Project",     icon: FolderKanban,   endpoint: "/projects",         labelKey: "title",       codeKey: "code" },
  { key: "customer",    label: "Customer",    icon: Building2,      endpoint: "/customers",        labelKey: "name" },
  { key: "lead",        label: "Lead",        icon: Users,          endpoint: "/leads",            labelKey: "company",     codeKey: "full_name" },
  { key: "site",        label: "Site",        icon: MapPin,         endpoint: "/customer-sites",   labelKey: "name" },
];

const AUDIENCES = [
  { key: "customer", label: "Customer" },
  { key: "internal", label: "Internal" },
];
const LENGTHS = [
  { key: "short",  label: "Short" },
  { key: "medium", label: "Medium" },
  { key: "long",   label: "Long" },
];

export default function CreateReportModal({
  sessionId, sessionTitle, onClose, onGenerated,
}) {
  const [kinds, setKinds] = useState([]);
  const [kind, setKind] = useState("engineering_note");
  const [title, setTitle] = useState(sessionTitle || "");
  const [linkKind, setLinkKind] = useState("jobcard");
  const [linkId, setLinkId] = useState("");
  const [linkLabel, setLinkLabel] = useState("");
  const [searchQ, setSearchQ] = useState("");
  const [rows, setRows] = useState([]);
  const [rowsLoading, setRowsLoading] = useState(false);
  const [family, setFamily] = useState(null);
  const [famChain, setFamChain] = useState("");
  const [famLoading, setFamLoading] = useState(false);
  const [audience, setAudience] = useState("customer");
  const [length, setLength] = useState("medium");
  const [extra, setExtra] = useState("");
  const [alsoPlanning, setAlsoPlanning] = useState(true);
  const [busy, setBusy] = useState(false);

  // ── Load available report kinds once ─────────────────────────
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/engineering/report-kinds");
        setKinds(data?.items || []);
      } catch (e) {
        toast.error("Could not load report kinds.");
      }
    })();
  }, []);

  const spec = useMemo(
    () => LINK_KINDS.find((k) => k.key === linkKind) || LINK_KINDS[0],
    [linkKind],
  );

  // ── Live search for the picked entity kind ───────────────────
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setRowsLoading(true);
      try {
        const params = new URLSearchParams({ limit: "40" });
        if (searchQ) params.set("q", searchQ);
        const { data } = await api.get(`${spec.endpoint}?${params}`);
        if (cancelled) return;
        const items = Array.isArray(data) ? data : (data.items || []);
        setRows(items.slice(0, 40));
      } catch {
        if (!cancelled) setRows([]);
      } finally {
        if (!cancelled) setRowsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [linkKind, searchQ, spec.endpoint]);

  // ── Resolve the family chain as soon as an entity is picked ──
  useEffect(() => {
    if (!linkId) { setFamily(null); setFamChain(""); return; }
    let cancelled = false;
    (async () => {
      setFamLoading(true);
      try {
        const { data } = await api.get("/family/resolve", {
          params: { kind: linkKind, id: linkId },
        });
        if (cancelled) return;
        setFamily(data?.family || null);
        setFamChain(data?.chain || "");
      } catch {
        if (!cancelled) { setFamily(null); setFamChain(""); }
      } finally {
        if (!cancelled) setFamLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [linkKind, linkId]);

  const labelFor = (r) => {
    const code = spec.codeKey ? r[spec.codeKey] : null;
    const body = r[spec.labelKey] || r.name || r.id || "—";
    return code ? `${code} · ${body}` : body;
  };

  const submit = async () => {
    if (!title.trim()) { toast.error("Title is required."); return; }
    if (!linkId) { toast.error("Pick an entity to link this report to."); return; }
    if (!family?.customer_id) {
      toast.error(
        "The linked entity has no customer. Set one first — every " +
        "generated report must belong to a customer.",
      );
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(
        `/engineering/sessions/${sessionId}/generate-report`,
        {
          kind,
          title: title.trim(),
          link: { kind: linkKind, id: linkId },
          audience, length,
          extra_instruction: extra.trim() || null,
          also_open_planning: linkKind === "project" ? alsoPlanning : false,
        },
      );
      toast.success("Report generated.");
      onGenerated?.(data);
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Report generation failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[80] bg-black/60 flex items-start sm:items-center justify-center p-4 pt-10 sm:pt-4"
      onClick={onClose}
      data-testid="eng-create-report-modal"
    >
      <div
        className="hv-card w-full max-w-3xl max-h-[92vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-3 border-b border-white/10">
          <div className="flex items-center gap-2">
            <FileText className="w-4 h-4 text-[#1FB6C1]" />
            <div className="font-display font-bold text-sm">Create report from session</div>
          </div>
          <button
            onClick={onClose}
            className="hv-btn hv-btn-ghost text-xs"
            data-testid="eng-create-report-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto p-3 space-y-4">
          {/* ── 1. Report kind ──────────────────────────────── */}
          <div>
            <div className="text-[10px] uppercase tracking-wider text-[#a0aabf] mb-1.5">Report kind</div>
            <div className="flex gap-1 flex-wrap">
              {kinds.map((k) => (
                <button
                  key={k.kind}
                  onClick={() => setKind(k.kind)}
                  className={`text-[11px] px-2.5 py-1 rounded border transition ${
                    kind === k.kind
                      ? "border-[#1FB6C1] bg-[#1FB6C1]/10 text-[#1FB6C1]"
                      : "border-white/10 text-[#cbd2df] hover:border-[#1FB6C1]/40"
                  }`}
                  data-testid={`eng-create-report-kind-${k.kind}`}
                >
                  {k.label}
                </button>
              ))}
            </div>
          </div>

          {/* ── 2. Title ───────────────────────────────────── */}
          <div>
            <div className="text-[10px] uppercase tracking-wider text-[#a0aabf] mb-1.5">Title</div>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="hv-input text-sm w-full"
              placeholder="e.g. Bush Lodge pump assessment"
              data-testid="eng-create-report-title"
            />
          </div>

          {/* ── 3. Link entity picker ──────────────────────── */}
          <div>
            <div className="text-[10px] uppercase tracking-wider text-[#a0aabf] mb-1.5">Link this report to</div>
            <div className="flex gap-1 flex-wrap mb-2">
              {LINK_KINDS.map((k) => {
                const Icon = k.icon;
                return (
                  <button
                    key={k.key}
                    onClick={() => { setLinkKind(k.key); setLinkId(""); setLinkLabel(""); }}
                    className={`text-[11px] px-2 py-1 rounded border inline-flex items-center gap-1 transition ${
                      linkKind === k.key
                        ? "border-[#1FB6C1] bg-[#1FB6C1]/10 text-[#1FB6C1]"
                        : "border-white/10 text-[#cbd2df] hover:border-[#1FB6C1]/40"
                    }`}
                    data-testid={`eng-create-report-linkkind-${k.key}`}
                  >
                    <Icon className="w-3 h-3" />
                    {k.label}
                  </button>
                );
              })}
            </div>
            <input
              value={searchQ}
              onChange={(e) => setSearchQ(e.target.value)}
              className="hv-input text-sm w-full mb-2"
              placeholder={`Search ${spec.label.toLowerCase()}…`}
              data-testid="eng-create-report-search"
            />
            <div className="max-h-40 overflow-y-auto border border-white/10 rounded">
              {rowsLoading ? (
                <div className="p-3 text-xs text-[#a0aabf] inline-flex items-center gap-2">
                  <Loader2 className="w-3 h-3 animate-spin" /> Loading…
                </div>
              ) : rows.length === 0 ? (
                <div className="p-3 text-xs text-[#a0aabf]">No matches.</div>
              ) : (
                rows.map((r) => (
                  <button
                    key={r.id}
                    onClick={() => { setLinkId(r.id); setLinkLabel(labelFor(r)); }}
                    className={`w-full text-left px-2.5 py-1.5 text-[12px] border-b border-white/5 last:border-b-0 transition ${
                      linkId === r.id
                        ? "bg-[#1FB6C1]/15 text-[#1FB6C1]"
                        : "text-[#cbd2df] hover:bg-white/5"
                    }`}
                    data-testid={`eng-create-report-linkrow-${r.id}`}
                  >
                    {labelFor(r)}
                  </button>
                ))
              )}
            </div>
          </div>

          {/* ── 4. Family chain preview ────────────────────── */}
          {linkId && (
            <div
              className="rounded-md border border-[#1FB6C1]/25 bg-[#1FB6C1]/[0.06] p-2"
              data-testid="eng-create-report-family-preview"
            >
              <div className="text-[10px] uppercase tracking-wider text-[#1FB6C1] mb-1 flex items-center gap-1">
                <Sparkles className="w-3 h-3" />
                Family that will be stamped on this report
              </div>
              {famLoading ? (
                <div className="text-[11px] text-[#a0aabf] inline-flex items-center gap-2">
                  <Loader2 className="w-3 h-3 animate-spin" /> Resolving…
                </div>
              ) : famChain ? (
                <div className="text-[12px] text-[#cbd2df]">{famChain}</div>
              ) : (
                <div className="text-[11px] text-amber-400">
                  Could not resolve a customer from this entity. Set a customer on the linked entity first.
                </div>
              )}
            </div>
          )}

          {/* ── 5. Audience / Length ──────────────────────── */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-[#a0aabf] mb-1.5">Audience</div>
              <div className="flex gap-1">
                {AUDIENCES.map((a) => (
                  <button
                    key={a.key}
                    onClick={() => setAudience(a.key)}
                    className={`text-[11px] px-2.5 py-1 rounded border transition ${
                      audience === a.key ? "border-[#1FB6C1] bg-[#1FB6C1]/10 text-[#1FB6C1]" : "border-white/10 text-[#cbd2df]"
                    }`}
                    data-testid={`eng-create-report-audience-${a.key}`}
                  >
                    {a.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-[#a0aabf] mb-1.5">Length</div>
              <div className="flex gap-1">
                {LENGTHS.map((l) => (
                  <button
                    key={l.key}
                    onClick={() => setLength(l.key)}
                    className={`text-[11px] px-2.5 py-1 rounded border transition ${
                      length === l.key ? "border-[#1FB6C1] bg-[#1FB6C1]/10 text-[#1FB6C1]" : "border-white/10 text-[#cbd2df]"
                    }`}
                    data-testid={`eng-create-report-length-${l.key}`}
                  >
                    {l.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* ── 6. Extra instruction ──────────────────────── */}
          <div>
            <div className="text-[10px] uppercase tracking-wider text-[#a0aabf] mb-1.5">
              Extra instruction <span className="normal-case text-[#647087]">(optional)</span>
            </div>
            <DictTextarea
              value={extra}
              onChange={(e) => setExtra(e.target.value)}
              rows={2}
              className="hv-input text-sm w-full min-h-[54px] resize-y"
              placeholder="e.g. Emphasise safety findings and defer commercial detail to the SO."
              data-testid="eng-create-report-extra"
            />
          </div>

          {/* ── 7. Also open planning workbench (only for projects) ── */}
          {linkKind === "project" && linkId && (
            <label
              className="flex items-center gap-2 text-[12px] text-[#cbd2df] cursor-pointer select-none"
              data-testid="eng-create-report-planning-toggle"
            >
              <input
                type="checkbox"
                checked={alsoPlanning}
                onChange={(e) => setAlsoPlanning(e.target.checked)}
                className="accent-[#1FB6C1] h-3 w-3"
                data-testid="eng-create-report-planning-checkbox"
              />
              <Sparkles className="w-3 h-3 text-fuchsia-300" />
              Also open a new planning workbench on this project (transcript copied across)
            </label>
          )}
        </div>

        <div className="p-3 border-t border-white/10 flex items-center justify-between gap-2">
          <div className="text-[10px] text-[#647087]">
            The generated PDF is saved to Customer Documents under {family?.customer_name || "the customer"}.
          </div>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              disabled={busy}
              className="hv-btn hv-btn-ghost text-xs"
              data-testid="eng-create-report-cancel"
            >
              Cancel
            </button>
            <button
              onClick={submit}
              disabled={busy || !title.trim() || !linkId || !family?.customer_id}
              className="hv-btn hv-btn-primary text-xs inline-flex items-center gap-1"
              data-testid="eng-create-report-submit"
            >
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileText className="w-3.5 h-3.5" />}
              Generate report
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
